/* Helper for Google Tag Manager DataLayer interactions */

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}

// Initialize dataLayer
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
  }
}

export const pushToDataLayer = (event: string, data: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    // Remove undefined keys to keep dataLayer clean
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== undefined),
    )

    window.dataLayer.push({
      event,
      ...cleanData,
      timestamp: new Date().toISOString(),
    })
    // For debugging
    // console.log('GTM Event:', event, cleanData)
  }
}

export type ConsentSettings = {
  ad_storage: 'granted' | 'denied'
  ad_user_data: 'granted' | 'denied'
  ad_personalization: 'granted' | 'denied'
  analytics_storage: 'granted' | 'denied'
}

// Enhanced Conversions User Data Structure
export interface UserData {
  email?: string
  phone_number?: string
  address?: {
    first_name?: string
    last_name?: string
    street?: string
    city?: string
    region?: string
    postal_code?: string
    country?: string
  }
}

const getStoredConsent = (): { marketing: boolean; analytics: boolean } => {
  if (typeof window === 'undefined')
    return { marketing: false, analytics: false }
  try {
    const saved = localStorage.getItem('dp6-cookie-consent')
    return saved ? JSON.parse(saved) : { marketing: false, analytics: false }
  } catch {
    return { marketing: false, analytics: false }
  }
}

export const gtmEvents = {
  viewItemList: (items: any[]) => {
    pushToDataLayer('view_item_list', {
      ecommerce: {
        items,
      },
    })
  },
  selectItem: (item: any) => {
    pushToDataLayer('select_item', {
      ecommerce: {
        items: [item],
      },
    })
  },
  viewItem: (item: any) => {
    pushToDataLayer('view_item', {
      ecommerce: {
        items: [item],
      },
    })
  },
  addToCart: (item: any) => {
    pushToDataLayer('add_to_cart', {
      ecommerce: {
        currency: 'BRL',
        value: item.price * item.quantity,
        items: [item],
      },
    })
  },
  removeFromCart: (item: any) => {
    pushToDataLayer('remove_from_cart', {
      ecommerce: {
        currency: 'BRL',
        value: item.price * item.quantity,
        items: [item],
      },
    })
  },
  beginCheckout: (items: any[], value: number) => {
    pushToDataLayer('begin_checkout', {
      ecommerce: {
        currency: 'BRL',
        value,
        items,
      },
    })
  },
  addShippingInfo: (items: any[], value: number, shippingTier: string) => {
    pushToDataLayer('add_shipping_info', {
      ecommerce: {
        currency: 'BRL',
        value,
        shipping_tier: shippingTier,
        items,
      },
    })
  },
  addPaymentInfo: (items: any[], value: number, paymentType: string) => {
    pushToDataLayer('add_payment_info', {
      ecommerce: {
        currency: 'BRL',
        value,
        payment_type: paymentType,
        items,
      },
    })
  },
  purchase: (
    transactionId: string,
    value: number,
    items: any[],
    userData?: UserData,
    tax = 0,
    shipping = 0,
  ) => {
    const consent = getStoredConsent()
    // user_data is only sent if ad_user_data (marketing) consent is granted
    // This ensures compliance with consent mode policies
    const validUserData = consent.marketing && userData ? userData : undefined

    pushToDataLayer('purchase', {
      // Enhanced Conversions data
      user_data: validUserData,
      ecommerce: {
        transaction_id: transactionId,
        currency: 'BRL',
        value,
        tax,
        shipping,
        items,
      },
    })
  },
  generateLead: (method: string) => {
    pushToDataLayer('generate_lead', {
      method,
    })
  },
  custom: (eventName: string, data: any = {}) => {
    pushToDataLayer(eventName, data)
  },
  updateConsent: (consent: ConsentSettings) => {
    if (typeof window !== 'undefined') {
      if (typeof window.gtag !== 'function') {
        window.gtag = function () {
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer.push(arguments)
        }
      }
      window.gtag('consent', 'update', consent)
      pushToDataLayer('consent_update', consent)
    }
  },
}
